import { useState } from 'react'
import { Button, StatusMessage } from '@krp/ui-kit'
import type { SourceItem, SourcesLoad, SourceKinds } from '../../../shared/sourceOptions'
import type { CashierAccessSettings, CashierAccessSummaryItem, CashierInvitation, CashierLockout, CashierPolicy, CashierRmkOptions, CashierRmkSettings, CashierSourcePolicy, SourceCashier } from '../model/types'
import { CashierPinCard } from './CashierPinCard'
import { CashierRmkSettingsCard, type CashierRmkDraft } from './CashierRmkSettingsCard'
import { CashierRoster } from './CashierRoster'
import { CashierSourcePicker } from './CashierSourcePicker'
import './cashierAccess.css'

export interface CashiersScreenProps {
  sources: SourceItem[]
  /** Вид источника: метка (как на плитке), цвет и общий порядок источников; без него вид не показывается. */
  kinds?: SourceKinds
  /** Загрузка списка источников: ожидание и ошибка с «Повторить». */
  sourcesLoad?: SourcesLoad
  resourceId: string
  cashiers: SourceCashier[]
  /** Сводка входа по кассирам источника (RP-CT3). */
  summary?: CashierAccessSummaryItem[]
  cashierKey: string
  selectedSourceCashierId?: string
  search: string
  settings: CashierAccessSettings | undefined
  policy: CashierSourcePolicy | undefined
  /** Правила кода и блокировок с сервера. */
  rules?: CashierPolicy
  savePolicy: (provider: 'native' | 'local') => Promise<void>
  /** Настройки РМК выбранного кассира и список, из которого выбирать (RP-CT4); без них блок не показывается. */
  rmkSettings?: CashierRmkSettings
  rmkOptions?: CashierRmkOptions
  rmkLoading?: boolean
  saveRmk?: (draft: CashierRmkDraft) => Promise<void>
  canManage: boolean
  busy: boolean
  loading: boolean
  error: string | null
  saved: boolean
  source: (resourceId: string) => void
  find: (search: string) => void
  /** Пустой ключ — выбор снят («← к списку»). */
  choose: (cashierKey: string, sourceCashierId?: string) => void
  save: (provider: 'native' | 'local' | 'inherit', pin?: string) => Promise<void>
  resetPin: () => Promise<void>
  revoke: () => Promise<void>
  reload: () => void
  /** Блокировки кодов за сутки (уведомление администратору). */
  lockouts?: CashierLockout[]
  /** Снять блокировку кассира, не меняя код. */
  unlock?: (lockout: CashierLockout) => Promise<void>
  /** Выдать кассиру приглашение задать свой код. */
  invite?: () => Promise<CashierInvitation | null>
  /** Открыть страницу «Задать свой код» (для кассира с приглашением). */
  openSetCode?: () => void
}
/**
 * Вкладка «Кассиры»: источник, его кассиры и карточка выбранного кассира (код и настройки РМК). На широком экране список и карточка
 * рядом, на узком — список, затем карточка с кнопкой «← к списку».
 */
export function CashiersScreen(props: CashiersScreenProps) {
  const [rmkDirty, setRmkDirty] = useState(false)
  const [pending, setPending] = useState<{ key: string; id?: string } | null>(null)
  const selected = props.cashiers.find((cashier) => cashier.resourceId === props.resourceId && (props.selectedSourceCashierId ? cashier.sourceCashierId === props.selectedSourceCashierId : cashier.key === props.cashierKey))
  const choose = (key: string, id?: string) => {
    if (rmkDirty) { setPending(id === undefined ? { key } : { key, id }); return }
    props.choose(key, id)
  }
  const leave = () => { if (pending === null) return; setRmkDirty(false); props.choose(pending.key, pending.id); setPending(null) }
  const lockouts = props.lockouts ?? []
  return <section className="cashier-access cashiers" aria-label="Кассиры" data-mode={selected === undefined ? 'list' : 'card'}>
    <p>Здесь настраивается, как кассир источника (1С или AQSI) подтверждает себя кодом при входе в свою статистику и в РМК. Выберите источник — для всех его кассиров
      задаётся способ подтверждения по умолчанию: код самого источника или отдельный код приложения. Выберите кассира — для него можно поменять способ, задать или сбросить код,
      выдать приглашение задать свой код и настроить РМК: допуск, кассу и вид цены по умолчанию. Чеки здесь не смотрят, а кассиров из разных источников, которые один человек,
      объединяют в карточке кассира (пункт меню «Кассиры»).</p>
    {!props.canManage && <p role="status">Для настройки кассиров нужно право управления кассирами.</p>}
    {props.openSetCode !== undefined && <p>Вам выдали приглашение? <Button actionId="receipts.cashier-access.set-code" onClick={props.openSetCode}>Задать свой код</Button></p>}
    {props.error !== null && <StatusMessage kind="error" title="Не удалось изменить настройки" text={props.error} />}
    {props.saved && <p role="status">Настройки сохранены.</p>}
    {lockouts.length > 0 && <section className="cashier-access__lockouts" role="status" aria-label="Блокировки кодов за сутки">
      <h3>Коды кассиров блокировались за последние сутки</h3>
      <ul>{lockouts.map((item) => <li key={item.cashierId}>
        <strong>{item.cashierName || 'Кассир без имени'}</strong> · {item.resourceName} — блокировок: {item.count}, последняя {new Date(item.lastAt).toLocaleString('ru-RU')}
        {item.lockedUntil !== null && <> · <strong>🔒 заперт до {new Date(item.lockedUntil).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</strong>
          {props.canManage && props.unlock !== undefined && <> <Button actionId="receipts.cashier-access.unlock" disabled={props.busy} onClick={() => void props.unlock?.(item)}>Снять блокировку</Button></>}</>}
      </li>)}</ul>
      <p>Много блокировок подряд — признак подбора кода. Новый код кассиру снимает блокировку и сбрасывает нарастание.</p>
    </section>}
    <CashierSourcePicker sources={props.sources} {...(props.kinds === undefined ? {} : { kinds: props.kinds })} {...(props.sourcesLoad === undefined ? {} : { sourcesLoad: props.sourcesLoad })}
      resourceId={props.resourceId} policy={props.policy} savePolicy={props.savePolicy} canManage={props.canManage} busy={props.busy} source={props.source} />
    {props.resourceId !== '' && <div className="cashiers__body">
      <div className="cashiers__list">
        <CashierRoster cashiers={props.cashiers} summary={props.summary} resourceId={props.resourceId} search={props.search} selectedKey={props.cashierKey}
          {...(props.selectedSourceCashierId === undefined ? {} : { selectedSourceCashierId: props.selectedSourceCashierId })} busy={props.busy} loading={props.loading} find={props.find} choose={choose} />
      </div>
      {selected !== undefined && <div className="cashiers__card">
        <Button className="cashiers__back" actionId="receipts.cashier-access.back" onClick={() => choose('')}>← К списку кассиров</Button>
        {pending !== null && <div role="group" aria-label="Несохранённые изменения"><p>⚠ В настройках РМК есть несохранённые изменения. Перейти без сохранения?</p>
          <Button actionId="receipts.cashier-access.choose" onClick={leave}>Перейти без сохранения</Button>
          <Button actionId="receipts.cashier-access.choose" onClick={() => setPending(null)}>Остаться</Button>
        </div>}
        <CashierPinCard key={selected.sourceCashierId ?? selected.key} cashier={selected} settings={props.settings} policy={props.policy} {...(props.rules === undefined ? {} : { rules: props.rules })} canManage={props.canManage} busy={props.busy} loading={props.loading}
          save={props.save} resetPin={props.resetPin} revoke={props.revoke} reload={props.reload} {...(props.invite === undefined ? {} : { invite: props.invite })} />
        {props.saveRmk !== undefined && <CashierRmkSettingsCard key={`${selected.sourceCashierId ?? selected.key}:${props.rmkSettings?.version ?? 'x'}`} settings={props.rmkSettings} options={props.rmkOptions}
          canManage={props.canManage} busy={props.busy} loading={props.rmkLoading === true} save={props.saveRmk} onDirtyChange={setRmkDirty} />}
      </div>}
    </div>}
  </section>
}
